import React from 'react';
import {
    BaseCustomRequestProductProps,
    CustomRequestProduct,
    CustomRequestProductForm,
} from '@/CustomRequestProductChannel/components/CustomRequestProduct.tsx';
import { Price } from '@schuettflix/conversion';
import { CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA, NestedOrderProductResponse } from '@schuettflix/interfaces';
import { useFormattedActiveTaxClassesByMarket } from '@/clients/tax-classes/useTaxClasses';
import { useFulfillmentContext } from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { InlineLoadingSuspenseBoundary } from '@/shared/components/InlineLoadingSuspenseBoundary.tsx';
import { usePlatformOrganizationId } from '@/clients/platformInfo/usePlatformInfo.ts';
import { useFulfillmentMutations } from '@/modules/fulfillment/hooks/useFulfillmentMutations';
import { useTemplate } from '@/clients/template/useTemplate.ts';
import { getTemplateProductCharacteristics } from '@/CustomRequestProductChannel/utils/getTemplateProductCharacteristics.ts';
import { isTransportProduct } from '@/CustomRequestProductChannel/utils/isTransportProduct.ts';
import { isWasteProduct } from '@/shared/domain/product/isWasteProduct.ts';

export type FulfillmentCustomRequestProductManagerProps = Omit<
    BaseCustomRequestProductProps,
    'customerId' | 'taxInfo'
> & {
    product: BaseCustomRequestProductProps['product'] & Pick<NestedOrderProductResponse, 'orderProductGroupId'>;
    onCompleteChange: (productId: string, value: boolean) => void; // shared
};

export const FulfillmentCustomRequestProductManager: React.FC<FulfillmentCustomRequestProductManagerProps> = props => {
    const { setCanSubmit, order, updateFulfillmentProduct } = useFulfillmentContext();
    const { data: template } = useTemplate(props.templateId);
    const { freightType: templateFreightType, disposalPurpose: templateDisposalPurpose } =
        getTemplateProductCharacteristics({ template, productType: props.product.type }) ?? {};

    const orderingOrganizationId = order?.customerInfo?.organizationId;
    const platformOrganizationId = usePlatformOrganizationId();

    const {
        patchFulfillmentQuoteMutation: {
            isPending: patchFulfillmentQuoteIsPending,
            mutateAsync: mutateAsyncPatchFulfillmentQuote,
        },
        postFulfillmentQuoteMutation: {
            isPending: postFulfillmentQuoteIsPending,
            mutateAsync: mutateAsyncPostFulfillmentQuote,
        },
    } = useFulfillmentMutations();

    const taxInfo = useFormattedActiveTaxClassesByMarket();

    if (!platformOrganizationId || !orderingOrganizationId) return null;

    const handleSubmit = async (formValues: CustomRequestProductForm) => {
        if (patchFulfillmentQuoteIsPending || postFulfillmentQuoteIsPending || !order) {
            return;
        }

        try {
            if (props.quote?.id) {
                const body = {
                    orderingOrganizationId,
                    purchaseTaxClassId: formValues.purchaseTaxClassId,
                    salesTaxClassId: formValues.salesTaxClassId,
                    purchasePrice: new Price(formValues.purchasePrice, formValues.currencyCode).toNumber(),
                    amount: formValues.amount,
                    unit: formValues.unit,
                    partnerOrganizationId: formValues.partnerOrganizationId,
                    salesPrice: new Price(formValues.salesPrice, formValues.currencyCode).toNumber(),
                    serviceDate: new Date(formValues.serviceDate),
                    productCategory: props.product.category,
                    productType: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(props.product.type),
                    payload: {
                        name: formValues.productName,
                        freightType: formValues.freightType,
                        disposalPurpose: formValues.disposalPurpose,
                    },
                };

                await mutateAsyncPatchFulfillmentQuote({
                    body,
                    productId: props.product.id,
                });
            } else {
                if (order.humanReadableId === null) {
                    throw new Error('Order has no humanReadableId');
                }

                const fulfillmentQuoteToBeCreated = {
                    orderingOrganizationId,
                    purchaseTaxClassId: formValues.purchaseTaxClassId,
                    salesTaxClassId: formValues.salesTaxClassId,
                    purchasePrice: new Price(formValues.purchasePrice, formValues.currencyCode).toNumber(),
                    amount: formValues.amount,
                    unit: formValues.unit,
                    partnerOrganizationId: formValues.partnerOrganizationId,
                    platformOrganizationId,
                    currencyCode: formValues.currencyCode,
                    salesPrice: new Price(formValues.salesPrice, formValues.currencyCode).toNumber(),
                    serviceDate: new Date(formValues.serviceDate),
                    orderNumber: order.humanReadableId,
                    orderProductId: props.product.id,
                    productCategory: props.product.category,
                    productType: CUSTOM_REQUEST_PRODUCT_TYPES_SCHEMA.parse(props.product.type),
                    orderProductGroupId: props.product.orderProductGroupId,
                    payload: {
                        name: formValues.productName,
                        freightType: formValues.freightType,
                        disposalPurpose: formValues.disposalPurpose,
                    },
                };

                await mutateAsyncPostFulfillmentQuote(fulfillmentQuoteToBeCreated);
            }

            props.onCompleteChange(props.product.id, true);
        } catch (err) {
            console.error('handleSubmit: ', err);
            props.onCompleteChange(props.product.id, false);
        }

        props.onCompleteChange(props.product.id, true);
    };

    return (
        <InlineLoadingSuspenseBoundary>
            <CustomRequestProduct
                {...props}
                onUpdateProductConfiguration={productConfiguration => {
                    updateFulfillmentProduct?.(props.product.id, {
                        fulfillmentProductConfiguration: {
                            showZeroPurchasePrice:
                                productConfiguration.showZeroPurchasePrice ??
                                props.product.productConfiguration.showZeroPurchasePrice,
                            showZeroSalesPrice:
                                productConfiguration.showZeroSalesPrice ??
                                props.product.productConfiguration.showZeroSalesPrice,
                        },
                    });
                    return props.onUpdateProductConfiguration?.(productConfiguration);
                }}
                customerId={orderingOrganizationId}
                taxInfo={taxInfo}
                formFieldSettings={{
                    ...props.formFieldSettings,
                    freightType: {
                        ...props.formFieldSettings?.freightType,
                        isOptional: !isTransportProduct(props.product) && !templateFreightType,
                        isHidden: !isTransportProduct(props.product) || !!templateFreightType,
                    },
                    disposalPurpose: {
                        ...props.formFieldSettings?.disposalPurpose,
                        isOptional:
                            !isWasteProduct(props.product.type) &&
                            !templateDisposalPurpose &&
                            !props.quote?.disposalPurpose,
                        isHidden: !isWasteProduct(props.product.type) || !!templateDisposalPurpose,
                    },
                }}
                onFormChange={(...params) => {
                    const [form] = params;
                    if (form.isValid) {
                        void handleSubmit(form.values);
                        setCanSubmit(true);
                    } else {
                        // if we have errors, this section is not valid. By this callback we trigger uproot that this section is not valid.
                        props?.onCompleteChange(props.product.id, false);
                        setCanSubmit(false);
                    }
                    props.onFormChange?.(...params);
                }}
                restrictFutureServiceDate
            />
        </InlineLoadingSuspenseBoundary>
    );
};
